import { useEffect, useState } from 'react';
import { resolveSeoForRoute, SeoConfig } from '@/lib/seo';

interface SeoHeadProps {
  pageType: string;
  entity?: {
    id?: string;
    slug?: string;
    name?: string;
    title?: string;
    project_name?: string;
    description?: string;
    summary?: string;
    excerpt?: string;
    image_url?: string;
    headline?: string;
  };
}

export const SeoHead = ({ pageType, entity }: SeoHeadProps) => {
  const [seoConfig, setSeoConfig] = useState<SeoConfig | null>(null);

  useEffect(() => {
    const loadSeo = async () => {
      try {
        const config = await resolveSeoForRoute(pageType, entity);
        setSeoConfig(config);

        // Update document title
        document.title = config.title;

        // Update meta tags
        updateMetaTag('description', config.description);
        updateMetaTag('robots', `${config.robots.index ? 'index' : 'noindex'}, ${config.robots.follow ? 'follow' : 'nofollow'}`);

        // Update canonical
        updateLinkTag('canonical', config.canonicalUrl || '');

        // Update OpenGraph tags
        updateMetaTag('og:title', config.openGraph.title, 'property');
        updateMetaTag('og:description', config.openGraph.description, 'property');
        updateMetaTag('og:type', config.openGraph.type, 'property');
        updateMetaTag('og:url', config.openGraph.url || '', 'property');
        updateMetaTag('og:site_name', config.openGraph.siteName, 'property');
        if (config.openGraph.image) {
          updateMetaTag('og:image', config.openGraph.image, 'property');
        }

        // Update Twitter tags
        updateMetaTag('twitter:card', config.twitter.card);
        updateMetaTag('twitter:title', config.twitter.title);
        updateMetaTag('twitter:description', config.twitter.description);
        if (config.twitter.image) {
          updateMetaTag('twitter:image', config.twitter.image);
        }

        // Update JSON-LD
        updateJsonLd(config.jsonLd);
      } catch (error) {
        console.error('Failed to load SEO config:', error);
      }
    };

    loadSeo();
  }, [pageType, entity]);

  return null;
};

function updateMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name') {
  let element = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function updateLinkTag(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function updateJsonLd(schemas: Record<string, unknown>[]) {
  // Remove existing JSON-LD scripts
  document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());

  // Add new JSON-LD scripts
  schemas.forEach(schema => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}

export default SeoHead;
